/**
 * Shared admin API helper for AdminHub tabs.
 *
 * Uses the regular user JWT (localStorage.tactical_token). The backend's
 * verify_admin_token now accepts user JWTs belonging to super_admin users,
 * so all the legacy /api/admin/* endpoints work seamlessly.
 */

const apiBase = () => process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('tactical_token');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${tok()}`,
});

const handle = async (res) => {
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try { detail = (await res.json()).detail || detail; } catch (_e) { /* no-op */ }
    throw new Error(detail);
  }
  return res.json();
};

export const adminApi = {
  // ---------- Analytics / Executive / Active users ----------
  analytics: () => fetch(`${apiBase()}/api/admin/analytics`, { headers: authHeaders() }).then(handle),
  executiveReport: () => fetch(`${apiBase()}/api/admin/executive-report`, { headers: authHeaders() }).then(handle),
  activeUsers: () => fetch(`${apiBase()}/api/admin/active-users`, { headers: authHeaders() }).then(handle),

  // ---------- Activity logs ----------
  activityLogs: (filter = '', eventType = '') => {
    const params = new URLSearchParams();
    if (filter) params.set('search', filter);
    if (eventType) params.set('event_type', eventType);
    const qs = params.toString();
    return fetch(`${apiBase()}/api/admin/activity-logs${qs ? `?${qs}` : ''}`, { headers: authHeaders() }).then(handle);
  },
  activitySummary: () => fetch(`${apiBase()}/api/admin/activity-logs/summary`, { headers: authHeaders() }).then(handle),
  cleanupLogs: () =>
    fetch(`${apiBase()}/api/admin/activity-logs/cleanup`, { method: 'POST', headers: authHeaders() }).then(handle),

  // ---------- Manager requests (Invites) ----------
  managerRequests: () => fetch(`${apiBase()}/api/admin/manager-requests`, { headers: authHeaders() }).then(handle),
  deleteManagerRequest: (id) =>
    fetch(`${apiBase()}/api/admin/manager-requests/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  // ---------- Month / template preview ----------
  tacticalPreview: (year, month) => {
    const qs = year && month ? `?year=${year}&month=${month}` : '';
    return fetch(`${apiBase()}/api/admin/tactical-preview${qs}`, { headers: authHeaders() }).then(handle);
  },

  // ---------- TAH template ----------
  tahList: () => fetch(`${apiBase()}/api/admin/tah-template`, { headers: authHeaders() }).then(handle),
  tahCreate: (name) =>
    fetch(`${apiBase()}/api/admin/tah-template`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ name }),
    }).then(handle),
  tahUpdate: (id, data) =>
    fetch(`${apiBase()}/api/admin/tah-template/${id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(data),
    }).then(handle),
  tahDelete: (id) =>
    fetch(`${apiBase()}/api/admin/tah-template/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    }).then(handle),
  tahReorder: (itemIds) =>
    fetch(`${apiBase()}/api/admin/tah-template/reorder`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(itemIds),
    }).then(handle),

  // ---------- What's New banners ----------
  whatsNewList: () => fetch(`${apiBase()}/api/admin/whats-new`, { headers: authHeaders() }).then(handle),
  whatsNewCreate: (data) =>
    fetch(`${apiBase()}/api/admin/whats-new`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(data),
    }).then(handle),
  whatsNewUpdate: (id, data) =>
    fetch(`${apiBase()}/api/admin/whats-new/${id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(data),
    }).then(handle),
  whatsNewDelete: (id) =>
    fetch(`${apiBase()}/api/admin/whats-new/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    }).then(handle),

  // ---------- Impersonation + diagnostics ----------
  searchUsers: (q) => fetch(`${apiBase()}/api/admin/users?q=${encodeURIComponent(q || '')}`, { headers: authHeaders() }).then(handle),
  impersonate: (userId) =>
    fetch(`${apiBase()}/api/admin/impersonate/${userId}`, {
      method: 'POST', headers: authHeaders(),
    }).then(handle),
  diagnoseUser: (email) =>
    fetch(`${apiBase()}/api/admin/user-tacticals-diagnostic?email=${encodeURIComponent(email)}`, { headers: authHeaders() }).then(handle),
  archiveTactical: (tacticalId) =>
    fetch(`${apiBase()}/api/admin/archive-tactical/${tacticalId}`, { method: 'POST', headers: authHeaders() }).then(handle),
  restoreTactical: (tacticalId) =>
    fetch(`${apiBase()}/api/admin/restore-tactical/${tacticalId}`, { method: 'POST', headers: authHeaders() }).then(handle),
  deleteTactical: (tacticalId) =>
    fetch(`${apiBase()}/api/admin/tactical/${tacticalId}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  // ---------- Review initiator sync (consumed by AdminHub.js inline) ----------
  reviewInitiators: () => fetch(`${apiBase()}/api/admin-roles/review-initiators`, { headers: authHeaders() }).then(handle),
  syncReviewInitiators: () =>
    fetch(`${apiBase()}/api/admin-roles/review-initiators/sync-entra`, {
      method: 'POST', headers: authHeaders(),
    }).then(handle),
  // ---------- 5-Star categories ----------
  categoriesList: () => fetch(`${apiBase()}/api/five-star/categories?include_inactive=true`, { headers: authHeaders() }).then(handle),
  categoriesSeedDefaults: () =>
    fetch(`${apiBase()}/api/five-star/categories/seed-defaults`, { method: 'POST', headers: authHeaders() }).then(handle),
  categoryCreate: (data) =>
    fetch(`${apiBase()}/api/five-star/categories`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(data),
    }).then(handle),
  categoryUpdate: (id, data) =>
    fetch(`${apiBase()}/api/five-star/categories/${id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(data),
    }).then(handle),
  categoryDelete: (id) =>
    fetch(`${apiBase()}/api/five-star/categories/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    }).then(handle),
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) { return iso; }
};
